import { ValuePickerOption } from '../components/value-picker/value-picker'
import {
  ListDisplayKeys,
  ListRecord,
} from '../models/transaction-metadata.model'

/**
 * The ONE option model every lookup in the dialog reads (PVAL-26685).
 *
 * A static option (`{value, label, secondary?}`) and a row `/resolve` answered for a catalog or a
 * search (`{id, ticker, …}`) are both just records, read by the keys RPS serves beside them:
 * `valueKey` is what is written, `displayText` what the closed control shows, `displayColumns` what
 * the picker lays out. That replaces the old `optionsFrom` heuristics, which guessed an id and a
 * label off whatever keys a row happened to have.
 */

/**
 * How long the typing has to stop before a lookup asks. Long enough that a word is one request
 * rather than six, short enough that the panel still feels like it answers the keystroke. Shared by
 * the Edit dialog and the Bulk grid, so a lookup types the same in both.
 */
export const LOOKUP_DEBOUNCE_MS = 300

/** Reports a column a row did not carry. Called once per row and column; the caller de-duplicates. */
export type BlankCellReporter = (column: string) => void

/** A value as a cell reads it: blank for absent, the string otherwise. */
const cellText = (value: unknown): string =>
  value == null ? '' : typeof value === 'object' ? '' : String(value)

/** A JSON object held as text, parsed; any other value, as it came. */
const jsonObjectOf = (value: unknown): unknown => {
  if (typeof value !== 'string' || !value.trimStart().startsWith('{'))
    return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

/**
 * Read a dotted name off a record.
 *
 * The record's own key wins when it carries the dotted name literally; otherwise the name is
 * followed as a path (`security.ticker` → `record.security.ticker`). Either way an absent column is
 * `undefined`, which is what tells a missing column from an empty one.
 *
 * `intoJsonText` also follows the path through a string that holds a JSON object, which is how
 * business-ws sends `externalAccountParameters` (`"{\"glId\":4938}"`). Lists leave it off.
 */
export const readColumn = (
  record: ListRecord,
  column: string,
  intoJsonText = false,
): unknown => {
  if (Object.prototype.hasOwnProperty.call(record, column))
    return record[column]
  if (!column.includes('.')) return undefined
  return column.split('.').reduce<unknown>((value, key) => {
    const node = intoJsonText ? jsonObjectOf(value) : value
    return node && typeof node === 'object'
      ? (node as Record<string, unknown>)[key]
      : undefined
  }, record)
}

/**
 * Records as picker options.
 *
 * `[{id: 40554, ticker: 'AAPL'}]` with `{displayColumns: ['id','ticker'], displayText: 'id',
 * valueKey: 'id'}` reads as `[{value: '40554', text: '40554', cells: ['40554', 'AAPL']}]`.
 *
 * A record with no `valueKey` is skipped: an option that writes nothing is not a choice. A column a
 * record lacks renders as a `''` cell and is reported through `onBlank`, so a misnamed column is
 * said out loud rather than looking like data that was simply empty.
 */
export function normalizeOptions(
  records: readonly ListRecord[],
  keys: ListDisplayKeys,
  onBlank?: BlankCellReporter,
): ValuePickerOption[] {
  const options: ValuePickerOption[] = []
  for (const record of records) {
    if (record == null || typeof record !== 'object') continue
    const value = readColumn(record, keys.valueKey)
    if (value == null || typeof value === 'object') continue
    options.push({
      value: String(value),
      text: cellText(readColumn(record, keys.displayText)),
      cells: keys.displayColumns.map(column => {
        const cell = readColumn(record, column)
        if (cell === undefined) onBlank?.(column)
        return cellText(cell)
      }),
    })
  }
  return options
}

/**
 * A reporter that warns once per (field, column) for as long as it lives.
 *
 * The dialog holds one for its lifetime, so a column a whole search reply lacks is one line in the
 * console, not one per row per keystroke.
 */
export function blankCellLog(): (field: string) => BlankCellReporter {
  const warned = new Set<string>()
  return field => column => {
    const key = field + '\u0000' + column
    if (warned.has(key)) return
    warned.add(key)
    console.warn(
      `[transactions-2] lookup "${field}" names display column "${column}", which a resolved row ` +
        'does not carry; the cell renders blank. Check the field\u2019s displayColumns against the ' +
        'upstream row.',
    )
  }
}
